import React, { useState, useEffect } from 'react';

const FinancialAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [financialStats, setFinancialStats] = useState({});

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`
  };

  const authHeadersForForm = {
    'Authorization': `Bearer ${adminToken}`
  };

  const loadFinancialStats = async () => {
    try {
      const response = await fetch(`${API}/admin/financial-stats`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setFinancialStats(data);
      }
    } catch (error) {
      console.error('Failed to load financial stats:', error);
    }
  };

  useEffect(() => {
    loadFinancialStats();
  }, []);

  return (
                <div className="space-y-6">
                  <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    💰 Financial Dashboard
                  </h3>

                  {/* Sync Order History Button */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border-blue-500/30'
                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          🔄 Sync Order History
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Sync Order History with user membership plans. Safe operation - only creates missing records.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            showNotification('🔄 Syncing order history...', 'info');
                            const response = await fetch(`${API}/admin/orders/sync`, {
                              method: 'POST',
                              headers: authHeaders
                            });

                            if (response.ok) {
                              const result = await response.json();
                              if (result.success) {
                                showNotification(result.message, 'success');
                                // Reload financial data to show new orders
                                loadFinancialStats();
                              } else {
                                showNotification(`⚠️ ${result.message}`, 'warning');
                              }
                            } else {
                              showNotification('❌ Failed to sync orders', 'error');
                            }
                          } catch (error) {
                            showNotification('❌ Error: ' + error.message, 'error');
                          }
                        }}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold shadow-lg whitespace-nowrap"
                      >
                        🔄 SYNC NOW
                      </button>
                    </div>
                  </div>

                  {/* Revenue Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-green-900/40 to-green-800/20 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Total Revenue</p>
                        <span className="text-2xl">💵</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ${financialStats.total_revenue?.toFixed(2) || '0.00'}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>All-time earnings</p>
                    </div>

                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>This Month</p>
                        <span className="text-2xl">📅</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ${financialStats.month_revenue?.toFixed(2) || '0.00'}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Current month revenue</p>
                    </div>

                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Active Subs</p>
                        <span className="text-2xl">👥</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {financialStats.active_subscriptions || 0}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Paying customers</p>
                    </div>

                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>Total Orders</p>
                        <span className="text-2xl">🛒</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {financialStats.total_orders || 0}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Lifetime orders</p>
                    </div>
                  </div>

                  {/* Revenue by Plan */}
                  <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      💎 Revenue by Plan
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {financialStats.revenue_by_plan?.map((plan) => (
                        <div key={plan._id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex justify-between items-center">
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {plan._id === 'monthly' ? '📅 Monthly Pro' : '🎯 Yearly Pro'}
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {plan.count} orders
                              </p>
                            </div>
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              ${plan.total?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* KEY FINANCIAL METRICS ROW */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Average Order Value */}
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-teal-900/40 to-teal-800/20 border-teal-700' : 'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>Avg Order Value</p>
                        <span className="text-2xl">💰</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ${financialStats.average_order_value?.toFixed(2) || '0.00'}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>Per completed order</p>
                    </div>

                    {/* Failed Payments */}
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-red-900/40 to-red-800/20 border-red-700' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Failed Payments</p>
                        <span className="text-2xl">⚠️</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {financialStats.failed_payments_count || 0}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Requires attention</p>
                    </div>

                    {/* Churn Rate */}
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-gradient-to-br from-amber-900/40 to-amber-800/20 border-amber-700' : 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-300'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Churn Rate</p>
                        <span className="text-2xl">📉</span>
                      </div>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {financialStats.churn_rate?.toFixed(1) || '0.0'}%
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Last 30 days</p>
                    </div>
                  </div>

                  {/* Monthly Revenue Chart */}
                  <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📊 Monthly Revenue (Last 12 Months)
                    </h4>
                    <div className="space-y-2">
                      {financialStats.monthly_revenue_chart?.map((month, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className={`text-sm w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {month.month}
                          </span>
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-8 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full flex items-center justify-end pr-2"
                              style={{
                                width: `${Math.max(5, (month.revenue / Math.max(...(financialStats.monthly_revenue_chart?.map(m => m.revenue) || [1]))) * 100)}%`
                              }}
                            >
                              <span className="text-white text-xs font-semibold">${month.revenue?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Payment Methods Breakdown */}
                  <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      💳 Payment Methods Breakdown
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {financialStats.payment_methods_breakdown?.map((method, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border-2 ${
                          isDarkMode
                            ? 'bg-gradient-to-br from-indigo-900/30 to-indigo-800/10 border-indigo-700/50'
                            : 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-300'
                        }`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {method._id || 'Manual'}
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {method.count} transactions
                              </p>
                            </div>
                            <span className="text-2xl">
                              {method._id === 'Stripe' ? '💳' : method._id === 'PayPal' ? '🅿️' : '💵'}
                            </span>
                          </div>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            ${method.total?.toFixed(2) || '0.00'}
                          </p>
                          <div className={`mt-2 h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{
                                width: `${Math.max(10, (method.total / financialStats.total_revenue * 100))}%`
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        📝 Recent Transactions
                      </h4>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${API}/admin/export-financial-report`, {
                              headers: authHeaders
                            });

                            if (response.ok) {
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `financial_report_${new Date().toISOString().split('T')[0]}.csv`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                              showNotification('✅ Financial report exported successfully!', 'success');
                            } else {
                              showNotification('❌ Failed to export report', 'error');
                            }
                          } catch (error) {
                            showNotification('❌ Error: ' + error.message, 'error');
                          }
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm shadow-md flex items-center gap-2"
                      >
                        <span>📥</span> Export Report (CSV)
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <th className={`text-left py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date</th>
                            <th className={`text-left py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>User</th>
                            <th className={`text-left py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Plan</th>
                            <th className={`text-left py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount</th>
                            <th className={`text-left py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialStats.recent_transactions?.slice(0, 15).map((tx, idx) => (
                            <tr
                              key={idx}
                              className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}
                            >
                              <td className={`py-3 px-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className={`py-3 px-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {tx.user_email || 'N/A'}
                              </td>
                              <td className={`py-3 px-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {tx.plan_name || 'N/A'}
                              </td>
                              <td className={`py-3 px-2 text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ${tx.amount?.toFixed(2) || '0.00'}
                              </td>
                              <td className={`py-3 px-2 text-sm`}>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  tx.status === 'completed'
                                    ? isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                    : tx.status === 'failed'
                                    ? isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {tx.status || 'pending'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!financialStats.recent_transactions || financialStats.recent_transactions.length === 0) && (
                        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <p>No transactions yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Failed Payments Details */}
                  {financialStats.failed_payments && financialStats.failed_payments.length > 0 && (
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-300'}`}>
                      <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                        <span>⚠️</span> Failed Payments Tracking
                      </h4>
                      <div className="space-y-3">
                        {financialStats.failed_payments.slice(0, 5).map((payment, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30' : 'bg-white'} border ${isDarkMode ? 'border-red-800' : 'border-red-200'}`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {payment.user_email || 'Unknown user'}
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {payment.plan_name || 'N/A'} - ${payment.amount?.toFixed(2) || '0.00'}
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                                  {payment.created_at ? new Date(payment.created_at).toLocaleString() : 'N/A'}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-200 text-red-800'}`}>
                                FAILED
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className={`text-xs mt-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                        💡 Tip: Contact these customers to resolve payment issues and recover revenue
                      </p>
                    </div>
                  )}
                </div>
  );
};

export default FinancialAdmin;
